import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageShell from "@/components/PageShell";
import { RevealLines, FadeUp, WordReveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const chapters = [
  {
    n: "01",
    title: "A newcomer, engineered by veterans.",
    body:
      "NewGenPharma was founded in 2023 in Zug by a coalition of medicinal chemists, process engineers, and clinical scientists whose combined résumé spans three decades of API development at tier-one Swiss and German houses. The group is young; the hands are not.",
  },
  {
    n: "02",
    title: "Focused on the central nervous system.",
    body:
      "We concentrate our discovery on stimulants and cognition-modulating scaffolds — phenethylamines, tropane analogs, eugeroic modafinilines and next-generation aminoindanes — with an operational commitment to reproducible, scalable API intermediates.",
  },
  {
    n: "03",
    title: "Serving the intermediates market with rigor.",
    body:
      "Our commercial spine is the API-intermediates market. Every gram we ship carries a full CoA, ICH-Q3A/D residual solvent profile, and DMF-ready documentation. Precision is not a marketing word; it is the deliverable.",
  },
];

const stats = [
  { k: "2023", v: "Founded, Zug" },
  { k: "17", v: "Scientists on staff" },
  { k: "6", v: "Active discovery programs" },
  { k: "GMP", v: "Aligned facility, Basel-Landschaft" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImage = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yBig = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <PageShell title="NewGenPharma — Chemistry of the next nervous system">
      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative min-h-[92vh] px-6 md:px-10 pt-16 md:pt-24"
      >
        {/* Grid coordinates */}
        <div className="absolute top-24 right-6 md:right-10 font-mono-tight text-[10px] text-ink/50 text-right leading-relaxed hidden md:block">
          <div>47.1662° N</div>
          <div>08.5155° E</div>
          <div className="mt-2 text-oxred">● LIVE · ZUG, CH</div>
        </div>

        <div className="absolute left-6 md:left-10 top-32 font-mono-tight text-[10px] text-ink/50 hidden md:block">
          <div>NGP · EST. 2023</div>
          <div>DOSSIER / A—001</div>
        </div>

        <div className="mx-auto max-w-[1600px] grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-end min-h-[80vh] mt-8">
          <motion.div style={{ y: yBig }} className="lg:col-span-8">
            <FadeUp delay={0}>
              <div className="font-mono-tight text-[11px] text-ink/60 flex items-center gap-4">
                <span className="w-1.5 h-1.5 bg-oxred inline-block" />
                Swiss Pharmaceutical Research · Vol. II
              </div>
            </FadeUp>

            <RevealLines
              as="h1"
              className="font-display font-normal text-ink text-[16vw] md:text-[11vw] lg:text-[9.5vw] leading-[0.9] tracking-[-0.03em] mt-6"
              lines={["Chemistry", <>of the <em className="italic text-oxred">next</em></>, "nervous system."]}
              delay={0.15}
            />

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl">
              <FadeUp delay={0.8}>
                <p className="text-ink/75 text-base leading-relaxed">
                  We are a Swiss pharmaceutical research group designing, characterising and supplying
                  API intermediates for central-nervous-system stimulants — from phenethylamine scaffolds
                  to modafinil-class eugeroics.
                </p>
              </FadeUp>
              <FadeUp delay={0.95}>
                <div className="flex md:justify-end items-end h-full">
                  <Link
                    to="/what-we-do"
                    data-testid="hero-cta-explore"
                    className="group inline-flex items-center gap-3 font-mono-tight text-[11px] border-b border-ink pb-1"
                  >
                    Explore research
                    <ArrowUpRight
                      size={14}
                      className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </Link>
                </div>
              </FadeUp>
            </div>
          </motion.div>

          <motion.div
            style={{ y: yImage }}
            className="lg:col-span-4 relative h-[420px] md:h-[520px] w-full"
          >
            <motion.div
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
              className="absolute inset-0 overflow-hidden border border-ink/15"
            >
              <img
                src="https://images.unsplash.com/photo-1763668331599-487470fb85b2"
                alt="Pharmaceutical capsules"
                className="w-full h-full object-cover"
                style={{ filter: "contrast(1.02) saturate(0.85)" }}
              />
              <div className="absolute top-4 left-4 font-mono-tight text-[10px] text-bone bg-ink/70 px-2 py-1">
                FIG. 001 · NGP-107 · CAPSULE Ø 21.7mm
              </div>
              <div className="absolute bottom-4 right-4 font-mono-tight text-[10px] text-bone bg-ink/70 px-2 py-1">
                SAMPLE · BASEL LAB
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom hairline meta bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="mx-auto max-w-[1600px] mt-16 pt-6 border-t border-ink/15 grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {stats.map((s) => (
            <div key={s.k} className="flex flex-col">
              <span className="font-display text-3xl md:text-4xl leading-none">{s.k}</span>
              <span className="font-mono-tight text-[10px] text-ink/60 mt-2">{s.v}</span>
            </div>
          ))}
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="mt-24">
        <Marquee items={["API Intermediates", "CNS Stimulants", "Swiss Precision", "Since 2023"]} />
      </div>

      {/* MANIFESTO CHAPTERS */}
      <section data-testid="manifesto-section" className="px-6 md:px-10 py-32 md:py-48">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="flex items-baseline justify-between mb-24 border-b border-ink/15 pb-6">
              <span className="font-mono-tight text-[11px] text-ink/60">§ Manifesto</span>
              <span className="font-mono-tight text-[11px] text-ink/60">Three positions</span>
            </div>
          </FadeUp>

          <div className="space-y-32 md:space-y-48">
            {chapters.map((c) => (
              <div key={c.n} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
                <div className="md:col-span-3">
                  <FadeUp>
                    <div className="font-mono text-[80px] md:text-[120px] leading-none text-ink tracking-tight">
                      {c.n}
                    </div>
                    <div className="mt-3 h-px w-16 bg-oxred" />
                  </FadeUp>
                </div>
                <div className="md:col-span-9 md:pl-8">
                  <FadeUp delay={0.1}>
                    <h3 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-[22ch]">
                      {c.title}
                    </h3>
                  </FadeUp>
                  <FadeUp delay={0.25}>
                    <p className="mt-8 text-ink/75 text-base md:text-lg leading-relaxed max-w-2xl">
                      {c.body}
                    </p>
                  </FadeUp>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROGRAM */}
      <section className="px-6 md:px-10 pb-32">
        <div className="mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-8">
          <FadeUp className="md:col-span-5">
            <span className="font-mono-tight text-[11px] text-ink/60">§ Featured Program</span>
            <h3 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-tight mt-6">
              <WordReveal text="NGP-107" />
              <br />
              <em className="italic text-oxred text-4xl md:text-5xl">phenethylamine class</em>
            </h3>
            <p className="mt-10 text-ink/75 leading-relaxed max-w-md">
              A next-generation, selective norepinephrine-dopamine releaser optimised for lower cardiovascular
              liability. Currently in preclinical safety-pharmacology with a partner CRO in Allschwil.
            </p>
            <Link
              to="/what-we-do"
              data-testid="home-featured-cta"
              className="inline-flex items-center gap-2 font-mono-tight text-[11px] mt-10 border-b border-ink pb-1"
            >
              See full pipeline <ArrowUpRight size={14} />
            </Link>
          </FadeUp>

          <FadeUp className="md:col-span-7" delay={0.15}>
            <div className="relative aspect-[4/3] overflow-hidden border border-ink/15 bg-bone-2">
              <img
                src="https://images.unsplash.com/photo-1656331797721-b593b8f00297"
                alt="Molecular structure"
                className="w-full h-full object-cover"
                style={{ mixBlendMode: "multiply", filter: "grayscale(0.25)" }}
              />
              <div className="absolute top-4 left-4 font-mono-tight text-[10px] text-ink bg-bone/80 px-2 py-1">
                MOL / C11H15NO2 · MW 209.24
              </div>
              <div className="absolute bottom-4 right-4 font-mono-tight text-[10px] text-ink bg-bone/80 px-2 py-1">
                RENDER · DFT-B3LYP
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </PageShell>
  );
}
